<template>
    <div
        v-if="healthSurveyReports"
        class="container-fluid adminGroupHealthSurveyReportsWrapper">
        <div class="d-block d-md-flex justify-content-between mb-3">
            <div class="d-block d-md-flex flex-wrap align-items-center">
                <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                    <div class="input-group input-group-sm">
                        <select
                            v-model="search.health_survey_template_id"
                            @change="onChangeSearchHealthSurveyTemplateId"
                            class="form-control"
                            :class="{ 'is-invalid' : errors.health_survey_template_id }">
                            <option
                                key="health_survey_template_id"
                                :value="null">
                                質問デッキで検索
                            </option>
                            <option
                                v-for="(template, index) in healthSurveyTemplates"
                                :key="`health_survey_template_id_${index}`"
                                :value="template.id">
                                {{ template.name }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="mt-2 mb-2 ml-md-2 mb-md-0">
                    <div class="input-group input-group-sm">
                        <select
                            v-model="search.year"
                            @change="onChangeSearchYear"
                            class="form-control"
                            :class="{ 'is-invalid' : errors.year }">
                            <option
                                key="year"
                                :value="null">
                                集計した年で検索
                            </option>
                            <option
                                v-for="(year, index) in years"
                                :key="`year_${index}`"
                                :value="year">
                                {{ `${year}年` }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
        </div>

        <div class="row justify-content-center">
            <div class="col-md-12">
                <b-table
                    striped
                    hover
                    :items="healthSurveyReports"
                    :fields="fields"
                    :sort-by.sync="state.sortBy"
                    :sort-desc.sync="state.sortDesc"
                    @sort-changed="fetchHealthSurveyReports"
                    :responsive="true"
                    class="table-bordered m-table">
                    <template v-slot:cell(id)="data">
                        {{ data.item.id }}
                    </template>

                    <template v-slot:cell(term_start)="data">
                        {{ `${helper.dateForHuman(data.item.term_start, 'Asia/Tokyo')}〜${helper.dateForHuman(data.item.term_end, 'Asia/Tokyo')}` }}
                    </template>

                    <template v-slot:cell(health_survey_template_ids)="data">
                        {{ templateNames(data.item) }}
                    </template>

                    <template v-slot:cell(actions)="data">
                        <div class="d-flex justify-content-center align-items-center">
                            <b-button
                                class="btn btn-sm"
                                variant="success"
                                :href="`/admin/health_survey_reports/${data.item.id}`">
                                レポートを見る
                            </b-button>
                        </div>
                    </template>
                </b-table>

                <div class="d-flex flex-wrap justify-content-between align-items-center">
                    <b-pagination
                        :total-rows="state.total"
                        :per-page="state.per_page"
                        v-model="state.current_page"
                        @change="fetchHealthSurveyReports">
                    </b-pagination>

                    <div class="d-flex flex-wrap align-items-center">
                        <div class="mr-3 mb-3">
                            <span class="small">{{ dispPaginationDetail }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import constants from '../../../constants'
import YokumiruHelper from '../../../YokumiruHelper'
import moment from 'moment-timezone'

export default {
    props: {
        group: {
            type: Object,
            required: true,
        },
    },

    data: function () {
        return {
            helper: YokumiruHelper,
            search: {
                health_survey_template_id: null,
                year: null,
            },
            healthSurveyTemplates: [],
            healthSurveyReports: null,
            inProgress: false,
            errors: {},
            fields: [
                {
                    key: 'id',
                    label: '#',
                    sortable: true,
                },
                {
                    key: 'title',
                    label: 'タイトル',
                    sortable: true,
                },
                {
                    key: 'term_start',
                    label: '期間',
                    sortable: true,
                },
                {
                    key: 'health_survey_template_ids',
                    label: '質問デッキ',
                    sortable: false,
                },
                {
                    key: 'actions',
                    label: '',
                    sortable: false,
                },
            ],
        }
    },

    computed: {
        state: function () {
            return this.$store.state.adminCompanyHealthSurveyReports
        },

        /**
         * ページング詳細表示
         * @returns {string}
         */
        dispPaginationDetail: function () {
            if (this.state.total < 1) {
                return ''
            }
            return `全${this.state.total}件中 ${this.state.from}～${this.state.to}件表示`
        },

        /**
         * 年のリスト
         * @return {array}
         */
        years: function () {
            const years = []
            const date = moment()

            while (date.year() > 2024) {
                years.push(date.year())
                date.subtract(1, 'years')
            }
            return years
        },
    },

    created: function () {
        this.initQueryFromState()
        this.initSearchCondition()
        this.fetchHealthSurveyTemplates()
        this.fetchHealthSurveyReports()
    },

    methods: {
        ...mapMutations({
            setQuery: 'adminCompanyHealthSurveyReports/setQuery',
            setQueries: 'adminCompanyHealthSurveyReports/setQueries',
        }),

        /**
         * 検索条件の初期化
         */
        initQueryFromState: function () {
            if (localStorage.getItem('yokumiruWeb')) {
                const data = JSON.parse(localStorage.getItem('yokumiruWeb'))
                if (data.adminCompanyHealthSurveyReports) {
                    this.setQueries(data.adminCompanyHealthSurveyReports)
                }
            }
        },

        /**
         * 検索条件の初期化
         */
        initSearchCondition: function () {
            this.search.health_survey_template_id = this.state.health_survey_template_id
            this.search.year = this.state.year
        },

        onChangeSearchHealthSurveyTemplateId: async function () {
            await this.setQuery({ key: 'health_survey_template_id', value: this.search.health_survey_template_id })
            await this.fetchHealthSurveyReports(1)
        },

        onChangeSearchYear: async function () {
            await this.setQuery({ key: 'year', value: this.search.year })
            await this.fetchHealthSurveyReports(1)
        },

        /**
         * メンタルチェック質問デッキ一覧取得
         */
        fetchHealthSurveyTemplates: function () {
            axios.get(constants.admin.health_survey_templates.endpoint.base)
                .then(({data}) => {
                    this.healthSurveyTemplates = data.data
                })
        },

        /**
         * メンタルチェック回答一覧取得
         *
         * @param {number|object} param
         */
        fetchHealthSurveyReports: async function (param = null) {
            this.healthSurveyReports = null

            if (Number.isInteger(param) && param > 0) {
                await this.setQuery({ key: 'current_page', value: param })
            }
            else if (param !== null && param.hasOwnProperty('sortBy')) {
                await this.setQuery({ key: 'sortBy', value: param.sortBy })
                await this.setQuery({ key: 'sortDesc', value: param.sortDesc })
            }

            const endpoint = constants.admin.groups.endpoint.base

            axios.get(`${endpoint}/${this.group.id}/health_survey_reports${this.buildQuery()}`)
                .then(({data}) => {
                    this.healthSurveyReports = data.data
                    this.setQueries(data.meta)
                })
        },

        /**
         * QueryString生成
         *
         * @param {boolean} paginate
         * @return {string}
         */
        buildQuery: function (paginate = true) {
            let query = '?'

            const state = this.state

            if (paginate) {
                query += `page=${state.current_page}`
                query += `&sort=${state.sortBy}`
                query += `&desc=${state.sortDesc}`
            }
            else {
                query += `sort=${state.sortBy}`
                query += `&desc=${state.sortDesc}`
            }

            if (state.health_survey_template_id) {
                query += `&health_survey_template_id=${state.health_survey_template_id}`
            }
            if (state.year) {
                query += `&year=${state.year}`
            }

            return query
        },

        /**
         * 質問デッキ表記
         * @param {object} report
         * @return {string}
         */
        templateNames: function (report) {
            return report.health_survey_templates.map((template) => template.name).join(', ')
        },
    },
}
</script>

<style lang="scss">
.adminGroupHealthSurveyReportsWrapper {
    .b-table {
        th, td {
            font-size: 0.75rem;
        }
    }
}
</style>
